'use client';

import clsx from 'clsx';
import React, {
  useCallback, useMemo, useRef, useState
} from 'react';
import { FiClipboard } from 'react-icons/fi';

import styles from './codeSelect.module.scss';

type Props = {
  value: string;
  ariaLabel?: string;
};

export default function CodeSelect( { value, ariaLabel = 'Copy to clipboard' }: Props ) {
  const inputRef = useRef<HTMLInputElement | null>( null );
  const [ copied, setCopied ] = useState( false );

  // guard against null/undefined values happening at runtime
  const sanitized = value || '';

  // Calculate input width based on content length
  // (css isn't 100% reliable for fitting the width with inputs)
  const inputWidth = useMemo( () => {
    const charCount = sanitized.length || 8;
    return `${ Math.max( charCount, 8 ) }ch`;
  }, [ sanitized ] );

  // on click of box (not button), select all text to make copy easy with keyboard shortcuts
  const selectAll = useCallback( () => {
    const el = inputRef.current;
    if ( !el ) return;
    el.focus();
    el.select();
  }, [] );

  const copyToClipboard = useCallback( async() => {
    try {
      await navigator.clipboard.writeText( sanitized );
      setCopied( true );
      // Reset the copied state after a moment
      window.setTimeout( () => setCopied( false ), 1200 );
    } catch { /** no op */ }
  }, [ sanitized ] );

  return (
    <div className={ clsx( styles.code_select_container ) }>
      <div className={ styles.inset_box }>
        <input
          ref={ inputRef }
          type="text"
          readOnly
          value={ sanitized || '' }
          onFocus={ selectAll }
          onClick={ selectAll }
          spellCheck={ false }
          autoComplete="off"
          autoCorrect="off"
          className={ styles.code_input }
          style={ { width: inputWidth } }
        />
        <button
          type="button"
          onClick={ copyToClipboard }
          className={ styles.copy_btn }
          aria-label={ ariaLabel }
          title={ ariaLabel }
        >
          <FiClipboard />
        </button>
      </div>
      <div className={ clsx( styles.copied_hint, copied && styles.copied_hint_visible ) }>
        Copied
      </div>
    </div>
  );
}